import { useCallback } from "react";
import { getSceneState } from "../../../stores/sceneStore";
import { useSelectionStore } from "../../../stores/selectionStore";
import { findBodyForJointUuid } from "../../../utils/bodyHierarchy";

export function useInspectContextMenuTarget({
  closeContextMenu,
  contextMenuType,
  contextMenuBodyUuid,
  contextMenuBodyUuids,
  contextMenuMeshIds,
  contextMenuGeomKeys,
  contextMenuSiteIds,
  contextMenuInertialKeys,
  contextMenuJointIds,
  contextMenuCameraIds,
}) {
  return useCallback(() => {
    const selectionActions = useSelectionStore.getState();
    const openInspector = () => window.dispatchEvent(new CustomEvent("open-properties-panel"));
    const selectIfChanged = (currentValue, selectFn, nextValue) => {
      if (currentValue === nextValue) return;
      selectFn(nextValue);
    };
    const selectBodyIfChanged = (bodyUuid, source) => {
      const current = useSelectionStore.getState();
      current.setLastSelectionSource(source);
      if (current.selectedBody === bodyUuid) {
        if (current.lastSelectionSource !== source) {
          current.selectBody(null);
          current.setLastSelectionSource(source);
          current.selectBody(bodyUuid);
        }
        return;
      }
      current.selectBody(bodyUuid);
    };

    switch (contextMenuType) {
      case "object":
        if (!contextMenuBodyUuid) return;
        selectBodyIfChanged(contextMenuBodyUuid, "sceneTreeObject");
        openInspector();
        break;
      case "body": {
        const bodyUuid = contextMenuBodyUuids[0] || null;
        if (!bodyUuid) return;
        selectBodyIfChanged(bodyUuid, "sceneTree");
        openInspector();
        break;
      }
      case "joint": {
        const jointUuid = contextMenuJointIds[0] || null;
        if (!jointUuid) return;
        const sceneState = getSceneState();
        if (sceneState.joints?.[jointUuid]?.kind === "free") {
          const bodyUuid = findBodyForJointUuid(sceneState.bodies || {}, jointUuid);
          if (!bodyUuid) return;
          selectBodyIfChanged(bodyUuid, "sceneTreeObject");
          openInspector();
          break;
        }
        selectionActions.setLastSelectionSource("sceneTree");
        selectIfChanged(selectionActions.selectedJoint, selectionActions.selectJoint, jointUuid);
        openInspector();
        break;
      }
      case "mesh": {
        const meshUuid = contextMenuMeshIds[0] || null;
        if (!meshUuid) return;
        selectionActions.setLastSelectionSource("sceneTree");
        selectIfChanged(selectionActions.selectedMesh, selectionActions.selectMesh, meshUuid);
        openInspector();
        break;
      }
      case "geom": {
        const geomKey = contextMenuGeomKeys[0] || null;
        if (!geomKey) return;
        selectionActions.setLastSelectionSource("sceneTree");
        selectIfChanged(selectionActions.selectedGeom, selectionActions.selectGeom, geomKey);
        openInspector();
        break;
      }
      case "site": {
        const siteUuid = contextMenuSiteIds[0] || null;
        if (!siteUuid) return;
        selectionActions.setLastSelectionSource("sceneTree");
        selectIfChanged(selectionActions.selectedSite, selectionActions.selectSite, siteUuid);
        openInspector();
        break;
      }
      case "camera": {
        const cameraUuid = contextMenuCameraIds[0] || null;
        if (!cameraUuid) return;
        selectionActions.setLastSelectionSource("sceneTree");
        selectIfChanged(selectionActions.selectedCamera, selectionActions.selectCamera, cameraUuid);
        openInspector();
        break;
      }
      case "inertial": {
        const inertialKey = contextMenuInertialKeys[0] || null;
        if (!inertialKey) return;
        selectionActions.setLastSelectionSource("sceneTree");
        selectIfChanged(
          selectionActions.selectedInertial,
          selectionActions.selectInertial,
          inertialKey
        );
        openInspector();
        break;
      }
      default:
        return;
    }

    closeContextMenu();
  }, [
    closeContextMenu,
    contextMenuBodyUuid,
    contextMenuBodyUuids,
    contextMenuCameraIds,
    contextMenuGeomKeys,
    contextMenuInertialKeys,
    contextMenuJointIds,
    contextMenuMeshIds,
    contextMenuSiteIds,
    contextMenuType,
  ]);
}
